// The right pane of Messages: one open conversation, a one-to-one chat or a group.
//
// It reads the conversation, keeps it fresh while the tab is in view, and sends text.
// The page mounts a new one for each conversation (it is keyed by the conversation),
// so nothing of one conversation is ever left on screen under another.
//
// Reading is told to the server ("read up to here") when the conversation is on screen
// in a tab the person is looking at, as the app does on opening a chat. The mark is
// shared with the phone: its count for this conversation drops too. Nothing is marked
// from a hidden tab, or from a window that is on screen but not in use.
//
// The privacy notice comes first. Until the person has accepted it the server sends no
// messages, and this pane shows the notice with one button instead.
//
// A group's header has a Group info button (GroupInfoDialog): its people, and leaving,
// renaming and adding or removing people. A change made there is read back at once,
// here and in the list, so the server's own line ("… left") shows without waiting.
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type { FormEvent, KeyboardEvent, ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Info, Send, Users } from 'lucide-react';
import ImageLightbox from '../ImageLightbox';
import {
  MAX_MESSAGE_CHARS, messageMediaUrl, messagesAPI, newClientMsgId,
  type ChatThread, type GroupDetail, type GroupThread, type Message, type SentMessage,
} from '../../services/messagesApi';
import GroupInfoDialog from './GroupInfoDialog';
import { MESSAGES_PATH, type OpenConversation } from './messagesPath';
import {
  addEarlier, firstMessages, initials, mergeNewest, messageTime, newestSeq, peopleLabel, sameCode, voiceLength,
  type HeldMessage,
} from './messageText';
import { NOTICE_ACCEPT, NOTICE_PARAGRAPHS, NOTICE_TITLE, NOTICE_VERSION } from './notice';
import { tabInView, useInViewPoll } from './useInViewPoll';

interface ConversationPaneProps {
  open: OpenConversation;
  /** The project selected in the top bar: the one the notice is accepted for. */
  projectId: number;
  /** The person's own employee code, lower case: whose messages sit on the right in a group. */
  me: string;
  /** The name the list already has for this conversation, shown until it answers for itself. */
  knownName: string | null;
  /** The conversation was read up to its newest message: the page lowers its count. */
  onRead: (open: OpenConversation, unreadCount: number) => void;
  /** A message was sent: the page reads its list again. */
  onSent: () => void;
  /** A group was changed from Group info (renamed, people added or removed): the page reads its list again. */
  onGroupChanged: () => void;
  /** The person left this group from Group info: the page closes it and reads its list again. */
  onLeftGroup: (groupId: string) => void;
  /**
   * The person has just started this conversation (New chat, New group) and the page
   * has put the keyboard on this pane: once it can be typed in, the keyboard goes to the
   * message box (or to the notice's button), unless the person has moved it elsewhere.
   */
  takeFocus?: boolean;
}

/** Where the pane stands. 'missing' is the server's one plain refusal: no such conversation, or not this person's. */
type Stage = 'loading' | 'ready' | 'notice' | 'missing' | 'unconfirmed' | 'failed';

// An open conversation is read again this often while the tab is in view.
const READ_AGAIN_EVERY_MS = 8_000;
// Within this many pixels of the foot, the person is reading the newest messages and
// the pane follows new ones down. Further up, it leaves them where they are.
const NEAR_FOOT_PX = 48;

const statusOf = (err: unknown): number | undefined =>
  (err as { response?: { status?: number } } | null)?.response?.status;
const bodyOf = (err: unknown): { message?: unknown; version?: unknown } | undefined =>
  (err as { response?: { data?: { message?: unknown; version?: unknown } } } | null)?.response?.data;

const SIGN_IN_LINE = 'Your sign-in could not be confirmed, so this cannot be shown.';
const QUIET_LINE = 'px-4 py-10 text-center text-sm text-gray-500';
const TRY_AGAIN = 'font-semibold text-primary-600 hover:text-primary-700 focus:outline-none focus-visible:underline';

export default function ConversationPane({
  open, projectId, me, knownName, onRead, onSent, onGroupChanged, onLeftGroup, takeFocus = false,
}: ConversationPaneProps): ReactElement {
  const isGroup = open.kind === 'group';
  const noticeId = useId();

  const [stage, setStage] = useState<Stage>('loading');
  const [head, setHead] = useState<{ name: string; people: number | null; other: string | null } | null>(null);
  const [messages, setMessages] = useState<HeldMessage[]>([]);
  // Messages the server took and stored nothing for. It answers them as sent, and so
  // does this pane: they stay at the foot for as long as the conversation is open.
  const [echoes, setEchoes] = useState<SentMessage[]>([]);
  /** Where the page before the oldest one on screen starts. Null: nothing earlier. */
  const [earlier, setEarlier] = useState<number | null>(null);
  const [loadingEarlier, setLoadingEarlier] = useState(false);
  const [earlierFailed, setEarlierFailed] = useState(false);
  const [noticeVersion, setNoticeVersion] = useState<number | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [acceptFailed, setAcceptFailed] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [photo, setPhoto] = useState<{ src: string; alt: string } | null>(null);
  const [infoOpen, setInfoOpen] = useState(false);

  const alive = useRef(true);
  // The page's two callbacks, as they are now: an answer that arrives late calls today's.
  const tellRead = useRef(onRead);
  const tellSent = useRef(onSent);
  useEffect(() => {
    tellRead.current = onRead;
    tellSent.current = onSent;
  }, [onRead, onSent]);
  // The messages on screen, for the answers that arrive later and join them.
  const shown = useRef<HeldMessage[]>([]);
  // One read at a time: a refresh that falls due while one is in flight is skipped.
  const reading = useRef(false);
  // A read asked for by a change (Group info) while one was in flight: that one may have
  // been answered before the change, so one more follows it.
  const readAgain = useRef(false);
  const readLatest = useRef<(how: 'open' | 'refresh') => Promise<boolean>>(async () => false);
  const infoButton = useRef<HTMLButtonElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const missingLine = useRef<HTMLParagraphElement>(null);
  // Counts the times the screen started over, so an older page asked for before that is dropped.
  const startedOver = useRef(0);
  // How far the server has been told this person has read.
  const readUpTo = useRef(0);
  const sendingNow = useRef(false);
  // The message being sent and its id. A second try of the same words goes under the
  // same id, so the server answers with the one it stored and never posts it twice.
  const pending = useRef<{ text: string; id: string } | null>(null);
  const box = useRef<HTMLTextAreaElement>(null);
  const acceptButton = useRef<HTMLButtonElement>(null);
  const tookFocus = useRef(false);
  const scroller = useRef<HTMLDivElement>(null);
  const nearFoot = useRef(true);
  // The height of the list just before earlier messages are added above it.
  const heightBefore = useRef<number | null>(null);

  const put = (next: HeldMessage[]) => {
    shown.current = next;
    setMessages(next);
  };

  const showNotice = (version: unknown) => {
    // No message may be on screen while the notice is waiting.
    put([]);
    setEchoes([]);
    setNoticeVersion(typeof version === 'number' ? version : null);
    setAcceptFailed(false);
    setStage('notice');
  };

  const markRead = (top: number) => {
    // Only what the person can be looking at counts as read: the tab in view AND the
    // window in use. A window that is only showing (a second monitor, behind another
    // window, an unattended desk) still counts as in view and is still kept fresh, and
    // would tell the sender each new message had been seen. Nothing listens for the
    // window being used again: every refresh comes through here, and a mark held back
    // is not counted as sent, so it goes out with the next one.
    if (top <= readUpTo.current || !tabInView() || !document.hasFocus()) return;
    readUpTo.current = top;
    const call = isGroup ? messagesAPI.markGroupRead(open.id, top) : messagesAPI.markChatRead(open.id, top);
    call.then(
      (res) => tellRead.current(open, Number(res?.data?.data?.unreadCount ?? 0)),
      // A lost mark is not chased: the next new message, or the next opening, sends it again.
      () => undefined,
    );
  };

  /**
   * Read the newest page: 'open' starts the screen, 'refresh' joins it to what is there.
   * Answers true when the server asked for the privacy notice instead of sending messages.
   */
  const read = useCallback(async (how: 'open' | 'refresh'): Promise<boolean> => {
    if (reading.current) return false;
    reading.current = true;
    try {
      const res = isGroup ? await messagesAPI.groupMessages(open.id) : await messagesAPI.chatMessages(open.id);
      if (!alive.current) return false;
      const body = res.data;
      if (isGroup) {
        const group = (body as GroupThread).group;
        // Messages are per project. A group of another project is not this page's to show.
        if (!group || Number(group.projectId) !== projectId) {
          put([]);
          setStage('missing');
          return false;
        }
        setHead({ name: group.title, people: group.memberCount, other: null });
      } else {
        // A one-to-one chat is per project too. One of another project is not this
        // page's to show, and it stops here: nothing is marked read, and no notice is
        // offered, because accepting it here would record it for the wrong project.
        // An older server does not say which project: the chat then opens as it did.
        const theirs = (body as ChatThread).projectId;
        if (theirs != null && Number(theirs) !== projectId) {
          put([]);
          setStage('missing');
          return false;
        }
        const other = (body as ChatThread).other;
        setHead({ name: other?.name || other?.empCode || '', people: null, other: other?.empCode ?? null });
      }
      if (body.requiresDisclosure) {
        showNotice(body.version);
        return true;
      }
      const page: Message[] = Array.isArray(body.data) ? body.data : [];
      const next = body.nextBeforeSeq ?? null;
      if (how === 'open') {
        put(firstMessages(page));
        setEarlier(next);
      } else {
        const merged = mergeNewest(shown.current, page, next !== null);
        if (merged.replaced) {
          startedOver.current += 1;
          setEarlier(next);
        }
        put(merged.messages);
      }
      setStage('ready');
      readUpTo.current = Math.max(readUpTo.current, Number(body.lastReadSeq ?? 0));
      markRead(newestSeq(shown.current));
      return false;
    } catch (err) {
      if (!alive.current) return false;
      const status = statusOf(err);
      if (status === 404) {
        // No longer this person's to read (removed from the group, or the tick is gone).
        put([]);
        setStage('missing');
      } else if (how === 'open') {
        setStage(status === 401 ? 'unconfirmed' : 'failed');
      }
      // A refresh that failed any other way leaves the screen as it is; the next one tries again.
      return false;
    } finally {
      reading.current = false;
      if (readAgain.current && alive.current) {
        readAgain.current = false;
        void readLatest.current('refresh');
      }
    }
    // put, showNotice and markRead read only refs, state setters and the three props
    // below, and the pane lives for one conversation: nothing else can go stale here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGroup, open.id, projectId]);
  useEffect(() => { readLatest.current = read; }, [read]);

  useEffect(() => {
    alive.current = true;
    void read('open');
    return () => { alive.current = false; };
  }, [read]);

  /**
   * Group info changed the group (or found it is no longer this person's): show the
   * name and the count it answered with, and read the conversation and the list again
   * now, so the server's own line is there without waiting for the next refresh.
   */
  const groupChanged = (detail: GroupDetail | null) => {
    if (detail) setHead({ name: detail.title, people: detail.memberCount, other: null });
    if (reading.current) readAgain.current = true;
    else void read('refresh');
    onGroupChanged();
  };

  // Group info closed without leaving: the keyboard goes back to the button that opened
  // it. That button is gone when the group turned out to be no longer this person's;
  // the keyboard then goes to the line that says so (or the conversation's name), so it
  // stays in the pane.
  const hadInfo = useRef(false);
  useEffect(() => {
    if (hadInfo.current && !infoOpen) (infoButton.current ?? missingLine.current ?? heading.current)?.focus();
    hadInfo.current = infoOpen;
  }, [infoOpen]);

  useInViewPoll(() => { void read('refresh'); }, READ_AGAIN_EVERY_MS, stage === 'ready');

  const retry = () => {
    setStage('loading');
    void read('open');
  };

  const loadEarlier = async () => {
    if (earlier === null || loadingEarlier) return;
    const round = startedOver.current;
    setLoadingEarlier(true);
    setEarlierFailed(false);
    try {
      const res = isGroup ? await messagesAPI.groupMessages(open.id, earlier) : await messagesAPI.chatMessages(open.id, earlier);
      if (!alive.current || round !== startedOver.current) return;
      const body = res.data;
      if (body.requiresDisclosure) {
        showNotice(body.version);
        return;
      }
      heightBefore.current = scroller.current?.scrollHeight ?? null;
      put(addEarlier(shown.current, Array.isArray(body.data) ? body.data : []));
      setEarlier(body.nextBeforeSeq ?? null);
    } catch {
      if (alive.current) setEarlierFailed(true);
    } finally {
      if (alive.current) setLoadingEarlier(false);
    }
  };

  const accept = async () => {
    if (accepting) return;
    setAccepting(true);
    setAcceptFailed(false);
    try {
      await messagesAPI.acceptNotice(projectId);
    } catch {
      if (alive.current) {
        setAcceptFailed(true);
        setAccepting(false);
      }
      return;
    }
    if (!alive.current) return;
    setAccepting(false);
    setStage('loading');
    const askedAgain = await read('open');
    // Accepted for the selected project, and the server still wants it accepted: the
    // conversation belongs to another project (a direct chat from an older server does
    // not say which one it is in). It is not this page's to show, and asking again
    // would only go round.
    if (askedAgain && alive.current) setStage('missing');
  };

  const send = async () => {
    const text = draft.trim();
    // The ref stops a second press that lands before the first has redrawn the button.
    if (!text || sendingNow.current) return;
    sendingNow.current = true;
    setSending(true);
    setRefusal(null);
    const mine = pending.current?.text === text ? pending.current : { text, id: newClientMsgId() };
    pending.current = mine;
    try {
      const res = isGroup
        ? await messagesAPI.sendToGroup(open.id, text, mine.id)
        : await messagesAPI.sendToChat(open.id, text, mine.id);
      pending.current = null;
      const sent = res?.data?.data;
      if (alive.current && sent) {
        setDraft('');
        nearFoot.current = true;
        if (typeof sent.seq === 'number') {
          const stored: Message = { ...sent, seq: sent.seq, senderName: sent.senderName ?? '' };
          put(mergeNewest(shown.current, [stored], false).messages);
          // The server counts a person's own message as read by them.
          readUpTo.current = Math.max(readUpTo.current, sent.seq);
        } else {
          setEchoes((was) => (was.some((e) => e.clientMsgId === sent.clientMsgId) ? was : [...was, sent]));
        }
      }
      tellSent.current();
    } catch (err) {
      if (!alive.current) return;
      const status = statusOf(err);
      const said = bodyOf(err);
      if (status === 428) {
        // The notice has to be accepted first. The words stay in the box for afterwards.
        showNotice(said?.version);
      } else if (status === 401) {
        setRefusal(SIGN_IN_LINE);
      } else if (status === 404) {
        setRefusal('This conversation is not available.');
      } else {
        // The server's own words: "Slow down a little…", "Message is too long…".
        setRefusal(typeof said?.message === 'string' && said.message.trim() ? said.message : 'Could not send. Try again.');
      }
    } finally {
      sendingNow.current = false;
      if (alive.current) setSending(false);
    }
  };

  // Just started from New: the keyboard goes where the person can carry on, once, and
  // only if it is still where the page put it (on this pane) or nowhere at all.
  useEffect(() => {
    if (!takeFocus || tookFocus.current) return;
    const target = stage === 'ready' ? box.current : stage === 'notice' ? acceptButton.current : null;
    if (!target) return;
    tookFocus.current = true;
    const at = document.activeElement;
    if (!at || at === document.body || at.contains(target)) target.focus();
  }, [takeFocus, stage]);

  // The box is switched off while a message is on its way, which drops the keyboard
  // out of it. Put it back, so the next message can be typed straight away.
  const wasSending = useRef(false);
  useEffect(() => {
    if (wasSending.current && !sending) box.current?.focus();
    wasSending.current = sending;
  }, [sending]);

  // Keep the newest message in view for a person who is reading the foot, and keep
  // the place of a person who has just loaded earlier messages above.
  useLayoutEffect(() => {
    const el = scroller.current;
    if (!el) return;
    if (heightBefore.current !== null) {
      el.scrollTop += el.scrollHeight - heightBefore.current;
      heightBefore.current = null;
    } else if (nearFoot.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages, echoes, stage]);

  const onScroll = () => {
    const el = scroller.current;
    if (el) nearFoot.current = el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_FOOT_PX;
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter sends and Shift+Enter makes a new line. Enter that only picks a word while
    // typing in another script (isComposing) is neither.
    if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing) return;
    e.preventDefault();
    void send();
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    void send();
  };

  const name = head?.name || knownName || '';
  const otherCode = head?.other ?? null;
  // In a one-to-one chat "mine" is "not the other person's", which the conversation
  // says by itself. A group has no such shortcut and goes by the person's own code.
  const isMine = (senderEmpCode: string): boolean =>
    (isGroup || !otherCode ? sameCode(senderEmpCode, me) : !sameCode(senderEmpCode, otherCode));

  const bubble = (mine: boolean) =>
    `rounded-2xl px-3 py-2 text-sm ${mine ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-900'}`;

  const line = (m: HeldMessage, before: HeldMessage | undefined): ReactElement => {
    if (m.kind === 'system') {
      // A line the server wrote ("A added B"): nobody's bubble.
      return <div key={m.id} className="py-1 text-center text-xs text-gray-500">{m.content}</div>;
    }
    const mine = isMine(m.senderEmpCode);
    const who = m.senderName || m.senderEmpCode;
    // In a group the name is printed once at the top of each person's run of messages.
    const named = isGroup && !mine && !(before && before.kind !== 'system' && sameCode(before.senderEmpCode, m.senderEmpCode));
    const full = messageMediaUrl(m.media?.path);
    const small = messageMediaUrl(m.media?.thumbnailPath) ?? full;
    const length = voiceLength(m.media?.durationMs);
    return (
      <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
        <div className={`flex max-w-[80%] flex-col ${mine ? 'items-end' : 'items-start'}`}>
          {named && <span aria-hidden="true" className="mb-0.5 px-1 text-xs font-semibold text-gray-600">{who}</span>}
          <div className={bubble(mine)}>
            {/* Who said it, for a screen reader: the side of the screen says it to everyone else. */}
            <span className="sr-only">{mine ? 'You' : who}: </span>
            {m.removed ? (
              <span className="italic opacity-80">This message was removed</span>
            ) : (
              <>
                {m.media?.kind === 'image' && full && small && (
                  <button
                    type="button"
                    onClick={() => setPhoto({ src: full, alt: `Photo from ${mine ? 'you' : who}` })}
                    aria-label="Open photo"
                    className="block overflow-hidden rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                  >
                    {/* One fixed small size, so the list does not jump as pictures arrive. */}
                    <img src={small} alt="Photo" loading="lazy" className="h-40 w-40 bg-gray-200 object-cover" />
                  </button>
                )}
                {m.media?.kind === 'voice' && full && (
                  <span className="flex items-center gap-2">
                    {/* The browser's own player. Nothing is fetched until it is played. */}
                    <audio controls preload="none" src={full} aria-label="Voice note" className="h-9 w-56 max-w-full" />
                    {length && <span className="text-xs opacity-80">{length}</span>}
                  </span>
                )}
                {m.content && <p className={`whitespace-pre-wrap break-words ${m.media ? 'mt-1.5' : ''}`}>{m.content}</p>}
              </>
            )}
          </div>
          <span className="mt-0.5 px-1 text-[11px] text-gray-400">{messageTime(m.createdAt)}</span>
        </div>
      </div>
    );
  };

  return (
    <>
      <header className="flex flex-none items-center gap-3 border-b border-gray-100 px-4 py-3">
        {/* On a narrow screen the list and the conversation take turns: this goes back to the list. */}
        <Link
          to={MESSAGES_PATH}
          className="-ml-1 flex flex-none items-center gap-1 rounded-md px-1.5 py-1 text-sm font-medium text-gray-600 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 lg:hidden"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Back
        </Link>
        <span
          aria-hidden="true"
          className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-primary-50 text-sm font-semibold text-primary-700"
        >
          {isGroup ? <Users className="h-4 w-4" /> : initials(name)}
        </span>
        <div className="min-w-0">
          <h2 ref={heading} tabIndex={-1} className="truncate text-base font-semibold text-gray-900 focus:outline-none">{name || 'Conversation'}</h2>
          {head?.people != null && <p className="text-xs text-gray-500">{peopleLabel(head.people)}</p>}
        </div>
        {/* Once the group has answered (its messages, or the notice before them). */}
        {isGroup && (stage === 'ready' || stage === 'notice') && (
          <button
            ref={infoButton}
            type="button"
            onClick={() => setInfoOpen(true)}
            aria-label="Group info"
            aria-haspopup="dialog"
            title="Group info"
            className="ml-auto flex h-9 w-9 flex-none items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          >
            <Info className="h-5 w-5" aria-hidden="true" />
          </button>
        )}
      </header>

      {stage === 'loading' && <p role="status" className={QUIET_LINE}>Loading…</p>}
      {stage === 'missing' && (
        <p ref={missingLine} tabIndex={-1} className={`${QUIET_LINE} focus:outline-none`}>This conversation is not available.</p>
      )}
      {(stage === 'failed' || stage === 'unconfirmed') && (
        <p className={QUIET_LINE}>
          {stage === 'failed' ? 'This conversation could not be loaded.' : SIGN_IN_LINE}{' '}
          <button type="button" onClick={retry} className={TRY_AGAIN}>Try again</button>
        </p>
      )}

      {stage === 'notice' && (
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          <section aria-labelledby={noticeId} className="mx-auto max-w-md rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <h3 id={noticeId} className="text-base font-semibold text-gray-900">{NOTICE_TITLE}</h3>
            {noticeVersion === NOTICE_VERSION ? (
              <>
                {NOTICE_PARAGRAPHS.map((words) => (
                  <p key={words} className="mt-3 text-sm leading-6 text-gray-700">{words}</p>
                ))}
                {acceptFailed && <p role="alert" className="mt-3 text-sm text-red-600">Could not record this. Try again.</p>}
                <button
                  ref={acceptButton}
                  type="button"
                  onClick={() => { void accept(); }}
                  disabled={accepting}
                  className="mt-5 w-full rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:opacity-60"
                >
                  {NOTICE_ACCEPT}
                </button>
              </>
            ) : (
              // The server wants a notice accepted that this page does not have the words of.
              <p className="mt-3 text-sm leading-6 text-gray-700">
                This notice has changed. Read and accept it in the ChannelPlex app on your phone, then open this conversation again.
              </p>
            )}
          </section>
        </div>
      )}

      {stage === 'ready' && (
        <>
          {/* relative: the words kept for a screen reader are positioned out of the flow,
              and without this they would sit outside the scrolling box and make the
              whole page scroll (seen in a browser). */}
          <div ref={scroller} onScroll={onScroll} className="relative min-h-0 flex-1 overflow-y-auto px-4 py-3">
            {earlier !== null && (
              <div className="flex justify-center pb-3">
                <button
                  type="button"
                  onClick={() => { void loadEarlier(); }}
                  disabled={loadingEarlier}
                  className="rounded-md border border-gray-300 px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  {loadingEarlier ? 'Loading…' : 'Load earlier'}
                </button>
              </div>
            )}
            {earlierFailed && <p role="alert" className="pb-3 text-center text-sm text-red-600">Earlier messages could not be loaded.</p>}
            {messages.length === 0 && echoes.length === 0 ? (
              <p className="py-6 text-center text-sm text-gray-500">No messages yet.</p>
            ) : (
              <div role="log" aria-label={name ? `Messages with ${name}` : 'Messages'} className="space-y-2">
                {messages.map((m, i) => line(m, messages[i - 1]))}
                {echoes.map((e) => (
                  <div key={e.clientMsgId} className="flex justify-end">
                    <div className="flex max-w-[80%] flex-col items-end">
                      <div className={bubble(true)}>
                        <span className="sr-only">You: </span>
                        <p className="whitespace-pre-wrap break-words">{e.content}</p>
                      </div>
                      <span className="mt-0.5 px-1 text-[11px] text-gray-400">{messageTime(e.createdAt)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <form onSubmit={onSubmit} className="flex-none border-t border-gray-100 p-3">
            {refusal && <p role="alert" className="mb-2 text-sm text-red-600">{refusal}</p>}
            <div className="flex items-end gap-2">
              <textarea
                ref={box}
                aria-label="Message"
                // Behind hover, not printed: the screen stays short.
                title="Enter sends. Shift+Enter makes a new line."
                placeholder="Write a message"
                rows={2}
                value={draft}
                maxLength={MAX_MESSAGE_CHARS}
                disabled={sending}
                onChange={(e) => {
                  // Cut here as well: pasted or scripted text can get past maxLength.
                  const words = e.target.value.slice(0, MAX_MESSAGE_CHARS);
                  // Other words in the box: the message that failed is given up, and so is
                  // its id. Kept, the same short reply typed again later would go under it,
                  // and a server that had stored the first one would take the new one for
                  // a second try of it and post nothing.
                  if (pending.current && words.trim() !== pending.current.text) pending.current = null;
                  setDraft(words);
                }}
                onKeyDown={onKeyDown}
                className="max-h-40 min-h-[2.75rem] flex-1 resize-none rounded-xl border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100 disabled:bg-gray-50"
              />
              <button
                type="submit"
                disabled={sending || !draft.trim()}
                className="flex h-11 flex-none items-center gap-1.5 rounded-xl bg-primary-600 px-4 text-sm font-semibold text-white transition-colors hover:bg-primary-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:opacity-50"
              >
                <Send className="h-4 w-4" aria-hidden="true" />
                Send
              </button>
            </div>
            {/* Said only near the limit, where it is useful. */}
            {draft.length >= MAX_MESSAGE_CHARS - 200 && (
              <p className="mt-1 text-right text-xs text-gray-400">{draft.length} / {MAX_MESSAGE_CHARS}</p>
            )}
          </form>
        </>
      )}

      {photo && <ImageLightbox src={photo.src} alt={photo.alt} isOpen onClose={() => setPhoto(null)} />}
      {isGroup && infoOpen && (
        <GroupInfoDialog
          groupId={open.id}
          projectId={projectId}
          me={me}
          onClose={() => setInfoOpen(false)}
          onChanged={groupChanged}
          onLeft={() => onLeftGroup(open.id)}
        />
      )}
    </>
  );
}
